import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa6';
import { useAppContext } from '../../../../shared/context/AppContext';
import { useAnalytics } from '../../../../shared/hooks/useAnalytics';
import { EVENTS } from '../../../../shared/services/analyticsEvents';
import { getUserId } from '../../../../shared/domain/services/userService';
import Sidebar from '../../../../shared/components/Sidebar';
import * as categoryService from '../../../promptLibrary/data/services/categoryService';
import * as promptSubmissionController from '../controllers/promptSubmissionController';
import { getAllOrgDomains } from '../../../../shared/utils/organizationDomainUtil';
import CategoryDropdown from '../components/CategoryDropdown';
import BackgroundTheoryInput from '../components/BackgroundTheoryInput';
import PromptFormatTabs from '../components/PromptFormatTabs';
import PersonaInput from '../../../../shared/promptForm/components/PersonaInput';
import FormatInput from '../../../../shared/promptForm/components/FormatInput';
import ContextInput from '../../../../shared/promptForm/components/ContextInput';
import TaskInput from '../../../../shared/promptForm/components/TaskInput';
import DosInput from '../../../../shared/promptForm/components/DosInput';
import DontsInput from '../../../../shared/promptForm/components/DontsInput';
import ExamplesInput from '../../../../shared/promptForm/components/ExamplesInput';
import Button from '../../../../shared/components/Button';
import '../../../../shared/promptForm/components/FormInput.css';
import './AddPromptScreen.css';

const AddPromptScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, organization } = useAppContext();
  const { trackEvent } = useAnalytics();
  const [categories, setCategories] = useState([]);
  // Manual options: STRUCTURED, FREE_TEXT
  const [promptFormat, setPromptFormat] = useState('FREE_TEXT');
  const [formData, setFormData] = useState({
    title: '',
    persona: '',
    format: '',
    context: '',
    task: '',
    dos: '',
    donts: '',
    examples: '',
    category: [],
    backgroundTheory: '',
    description: '',
    // Free text field
    freeText: '',
  });
  const [selectedOrgId, setSelectedOrgId] = useState(null); // null = global, otherwise orgId
  const [orgDomains, setOrgDomains] = useState([]);
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Check if user is admin
  const isAdmin = user?.role && user.role.toLowerCase() === 'admin';

  const loadCategories = useCallback(async () => {
    const orgId = organization?.id || organization?.orgId;
    // Always fetch categories for user's org OR global categories
    // Pass orgId explicitly (even if null) to enable OR mode in backend
    const cats = await categoryService.getAllCategories({ orgId: orgId || null, isGlobal: true });
    setCategories(cats);
  }, [organization]);

  const loadOrganizations = useCallback(async () => {
    if (!isAdmin) return;
    
    try {
      setLoadingOrgs(true);
      const orgs = await getAllOrgDomains();
      setOrgDomains(orgs);
    } catch (error) {
      console.error('Error loading organizations:', error);
    } finally {
      setLoadingOrgs(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    loadCategories();
    loadOrganizations();
    // Pre-fill form if coming from Prompt Engineer
    if (location.state?.formData) {
      const fd = location.state.formData;
      const description = typeof fd.description === 'string' ? fd.description : '';
      setFormData((prev) => ({
        ...prev,
        ...fd,
        description,
      }));
    }
    // Default: admins default to global, non-admins use their org
    if (isAdmin) {
      setSelectedOrgId(null); // Global
    } else {
      setSelectedOrgId(organization?.id || organization?.orgId || null);
    }
  }, [location.state, loadCategories, loadOrganizations, isAdmin, organization]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddCategory = async (categoryName) => {
    try {
      const orgId = organization?.id || organization?.orgId;
      // Always fetch categories for user's org OR global categories
      // Pass orgId explicitly (even if null) to enable OR mode in backend
      const filters = { orgId: orgId || null, isGlobal: true };
      
      // Check if category already exists
      const exists = await categoryService.categoryExists(categoryName, filters);
      
      if (exists) {
        // Category already exists, add it to selection if not already selected
        const currentCategories = Array.isArray(formData.category) ? formData.category : (formData.category ? [formData.category] : []);
        if (!currentCategories.includes(categoryName)) {
          handleInputChange('category', [...currentCategories, categoryName]);
        }
      } else {
        // Category doesn't exist, add it
        await categoryService.addCategory(categoryName, { orgId });
        const updatedCategories = await categoryService.getAllCategories(filters);
        setCategories(updatedCategories);
        // Add the newly added category to selection
        const currentCategories = Array.isArray(formData.category) ? formData.category : (formData.category ? [formData.category] : []);
        handleInputChange('category', [...currentCategories, categoryName]);
      }
    } catch (error) {
      console.error('Error adding category:', error);
      const errorMessage = error.message || 'Failed to add category. Please try again.';
      alert(`Failed to add category: ${errorMessage}`);
    }
  };

  const handleFormatChange = (format) => {
    // Clear format-specific validation errors when switching formats
    // Don't clear the data - allow users to switch back and forth
    setPromptFormat(format);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user || !organization) {
      alert('Please log in to add prompts');
      return;
    }

    if (!formData.title || !formData.category || (Array.isArray(formData.category) && formData.category.length === 0)) {
      alert('Please fill in title and at least one category');
      return;
    }

    // Validate format-specific required fields
    if (promptFormat === 'STRUCTURED') {
      if (!formData.persona || formData.persona.trim() === '') {
        alert('Please fill in Persona for Structured format');
        return;
      }
      if (!formData.format || formData.format.trim() === '') {
        alert('Please fill in Format for Structured format');
        return;
      }
      if (!formData.context || formData.context.trim() === '') {
        alert('Please fill in Context for Structured format');
        return;
      }
      if (!formData.task || formData.task.trim() === '') {
        alert('Please fill in Task for Structured format');
        return;
      }
      if (!formData.dos || formData.dos.trim() === '') {
        alert('Please fill in Do\'s for Structured format');
        return;
      }
      if (!formData.donts || formData.donts.trim() === '') {
        alert('Please fill in Don\'ts for Structured format');
        return;
      }
    } else if (promptFormat === 'FREE_TEXT') {
      if (!formData.freeText || formData.freeText.trim() === '') {
        alert('Please enter prompt text for Free Text format');
        return;
      }
    }

    setSubmitting(true);
    try {
      const userId = getUserId(user);
      if (!userId) {
        setSubmitting(false);
        alert('User ID not found. Please log in again.');
        return;
      }
      
      // Determine orgId and isGlobal based on selection
      const isGlobal = selectedOrgId === null;
      
      // If user wants to create global prompt, check if they're admin
      if (isGlobal && !isAdmin) {
        setSubmitting(false);
        alert('Only admin users can create global prompts.');
        return;
      }
      
      const submissionData = {
        ...formData,
        promptFormat: promptFormat,
      };

      // For admins, pass orgId or isGlobal flag
      // For non-admins, use their organization
      const targetOrgId = isAdmin ? (isGlobal ? null : selectedOrgId) : (organization?.id || organization?.orgId);

      // Debug logging
      console.log('[AddPromptScreen] Submitting prompt:', {
        isAdmin,
        selectedOrgId,
        isGlobal,
        targetOrgId,
        organizationId: organization?.id || organization?.orgId
      });

      const result = await promptSubmissionController.submitPromptToLibrary(
        submissionData,
        userId,
        targetOrgId,
        user, // Pass user from context to avoid extra API call
        isGlobal // Pass flag to indicate if this should be a global prompt
      );
      
      // Track prompt creation event
      if (result && result.prompt) {
        trackEvent(EVENTS.PROMPT_CREATED, {
          promptId: result.prompt.id || result.prompt.promptId,
          promptTitle: formData.title,
          categories: Array.isArray(formData.category) ? formData.category : [formData.category],
          isGlobal: isGlobal,
        });
      }
      
      navigate('/home', { replace: true });
    } catch (error) {
      console.error('Error submitting prompt:', error);
      const errorMessage = error.message || 'Error submitting prompt. Please try again.';
      alert(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleBackClick = () => {
    navigate('/home');
  };

  return (
    <div className="add-prompt-screen">
      <Sidebar>
        <div className="details-sidebar-content">
          <button
            className="back-button"
            onClick={handleBackClick}
            aria-label="Back to Prompt Library"
          >
            <FaArrowLeft className="back-icon" />
          </button>
        </div>
      </Sidebar>
      <div className="add-prompt-main">
        <div className="prompt-details-card">
          <div className="prompt-details-gradient-header"></div>
          <div className="prompt-details-card-content">
            <form onSubmit={handleSubmit} className="add-prompt-form">
              <div className="form-grid">
                {/* Format Tabs */}
                <div className="form-format-section">
                  <PromptFormatTabs
                    activeFormat={promptFormat}
                    onFormatChange={handleFormatChange}
                  />
                </div>

                {/* Manual form – title, category, description, format-specific content */}
                <div className="form-title-section">
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    className="editable-title-input headline-large"
                    placeholder="Enter prompt title"
                  />
                </div>

                <div className="form-category-section">
                  <CategoryDropdown
                    categories={categories}
                    value={formData.category}
                    onChange={(value) => handleInputChange('category', value)}
                    onAddNew={handleAddCategory}
                  />
                </div>

                <div className="form-input-group description-input-group">
                  <label className="form-label headline-medium">Description</label>
                  <textarea
                    className="form-textarea"
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder="Enter a description for this prompt..."
                    rows={3}
                  />
                </div>

                {/* Format-specific content (STRUCTURED / FREE_TEXT only) */}
                {promptFormat === 'STRUCTURED' && (
                  <>
                    {/* Persona */}
                    <PersonaInput
                      value={formData.persona}
                      onChange={(value) => handleInputChange('persona', value)}
                    />

                    {/* Format */}
                    <FormatInput
                      value={formData.format}
                      onChange={(value) => handleInputChange('format', value)}
                    />

                    {/* Context */}
                    <ContextInput
                      value={formData.context}
                      onChange={(value) => handleInputChange('context', value)}
                    />

                    {/* Task */}
                    <TaskInput
                      value={formData.task}
                      onChange={(value) => handleInputChange('task', value)}
                    />

                    {/* Dos and Don'ts */}
                    <div className="form-row">
                      <DosInput
                        value={formData.dos}
                        onChange={(value) => handleInputChange('dos', value)}
                      />
                      <DontsInput
                        value={formData.donts}
                        onChange={(value) => handleInputChange('donts', value)}
                      />
                    </div>

                    {/* Examples */}
                    <ExamplesInput
                      value={formData.examples}
                      onChange={(value) => handleInputChange('examples', value)}
                    />
                  </>
                )}

                {promptFormat === 'FREE_TEXT' && (
                  <div className="form-input-group">
                    <label className="form-label headline-medium">Prompt Text</label>
                    <textarea
                      className="form-textarea"
                      value={formData.freeText}
                      onChange={(e) => handleInputChange('freeText', e.target.value)}
                      placeholder="Enter your prompt text here..."
                      rows={12}
                    />
                  </div>
                )}

                {/* Background Theory (manual formats only) */}
                <BackgroundTheoryInput
                  value={formData.backgroundTheory}
                  onChange={(value) => handleInputChange('backgroundTheory', value)}
                />
              </div>

              <div className="form-actions">
                {/* Organization Assignment (Admin Only) */}
                {isAdmin && (
                  <div className="form-input-group" style={{ marginBottom: '1rem' }}>
                    <label className="form-label headline-medium">Organization Assignment</label>
                    <select
                      value={selectedOrgId || ''}
                      onChange={(e) => setSelectedOrgId(e.target.value === '' ? null : e.target.value)}
                      className="form-input"
                      disabled={loadingOrgs || submitting}
                      style={{ cursor: loadingOrgs ? 'wait' : 'pointer' }}
                    >
                      <option value="">Global (all organizations)</option>
                      {orgDomains.map((org) => (
                        <option key={org.orgId} value={org.orgId}>
                          {org.domain}
                        </option>
                      ))}
                    </select>
                    <p style={{ fontSize: '0.875rem', color: '#666', marginTop: '0.25rem' }}>
                      {selectedOrgId === null 
                        ? 'Global prompts are visible to all users regardless of organization. Vote counts are organization-specific.'
                        : `This prompt will be assigned to ${orgDomains.find(o => o.orgId === selectedOrgId)?.domain || selectedOrgId}. Only users from this organization will see it.`
                      }
                    </p>
                  </div>
                )}
                
                <Button
                  type="submit"
                  variant="primary"
                  disabled={submitting}
                  className="add-prompt-submit-button"
                >
                  {submitting ? 'Adding...' : 'Add Prompt'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddPromptScreen;
